// ============================================================================
// Copia local para la caja de contingencia (modo contingencia, paso 3b)
//
// Mientras hay internet, cada 2 minutos se guarda en el PC lo que la caja de
// contingencia necesita para trabajar sin red: la carta con precios de la
// sede, las mesas y los pedidos abiertos con sus líneas.
//
// Reglas:
//   - Todo o nada: si una de las consultas falla, NO se toca la copia buena
//     que ya había. Una copia a medias (mesas nuevas con carta vieja) es peor
//     que una copia vieja entera, porque parece completa.
//   - Las líneas de TODOS los pedidos abiertos se vuelven a pedir cada vez.
//     Comparar totales no alcanza: cambiar una gaseosa de $5.000 por otra de
//     $5.000 deja el total igual y la copia mentiría.
//
// Sin React ni fetch: las consultas y el guardado se inyectan, así se prueba
// con node (src/lib/copia-local.test.ts). El cableado real está en
// app/components/copia-local-contingencia.tsx.
// ============================================================================

export const COPIA_CADA_MS = 2 * 60_000;
/** Pasado esto, la caja de contingencia avisa que los datos pueden estar viejos. */
export const COPIA_VIEJA_MS = 30 * 60_000;
const PEDIDOS_EN_PARALELO = 4;

export interface CopiaLocal<Producto = unknown, Mesa = unknown, Pedido = unknown> {
  version: 1;
  sedeId: number;
  /** Cuándo se tomó (ms). */
  tomadaEn: number;
  menu: Producto[];
  mesas: Mesa[];
  /** Pedidos abiertos, cada uno con sus líneas. */
  pedidos: Pedido[];
}

export interface FuentesCopia<Producto, Mesa, Resumen extends { id: number }, Pedido> {
  menu: (sedeId: number) => Promise<Producto[]>;
  mesas: (sedeId: number) => Promise<Mesa[]>;
  pedidosAbiertos: (sedeId: number) => Promise<Resumen[]>;
  /** Pedido con sus líneas. */
  detalle: (pedidoId: number) => Promise<Pedido>;
  guardar: (clave: string, copia: CopiaLocal<Producto, Mesa, Pedido>) => Promise<void>;
  ahora?: () => number;
}

export const claveCopia = (sedeId: number) => `copia:sede:${sedeId}`;

async function enLotes<T, R>(lista: T[], n: number, fn: (x: T) => Promise<R>): Promise<R[]> {
  const out: R[] = new Array(lista.length);
  let i = 0;
  const trabajador = async () => {
    while (i < lista.length) {
      const k = i++;
      out[k] = await fn(lista[k]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(n, lista.length) }, trabajador));
  return out;
}

/**
 * Toma la copia y la guarda. Si algo falla, lanza y no guarda nada.
 * Devuelve la copia guardada.
 */
export async function tomarCopia<Producto, Mesa, Resumen extends { id: number }, Pedido>(
  sedeId: number,
  f: FuentesCopia<Producto, Mesa, Resumen, Pedido>,
): Promise<CopiaLocal<Producto, Mesa, Pedido>> {
  const ahora = f.ahora ?? Date.now;
  const tomadaEn = ahora();
  const [menu, mesas, abiertos] = await Promise.all([f.menu(sedeId), f.mesas(sedeId), f.pedidosAbiertos(sedeId)]);
  const pedidos = await enLotes(abiertos, PEDIDOS_EN_PARALELO, (p) => f.detalle(p.id));
  const copia: CopiaLocal<Producto, Mesa, Pedido> = { version: 1, sedeId, tomadaEn, menu, mesas, pedidos };
  await f.guardar(claveCopia(sedeId), copia);
  return copia;
}

export type FrescuraCopia = 'sin_copia' | 'fresca' | 'vieja';

export function frescuraCopia(tomadaEn: number | null | undefined, ahora: number): FrescuraCopia {
  if (!tomadaEn) return 'sin_copia';
  return ahora - tomadaEn > COPIA_VIEJA_MS ? 'vieja' : 'fresca';
}
